import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { handleGetBlogById } from "../../shared/controllers/blogController";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface Blog {
    title: string;
    description: string;
    image?: string;
    created_at: string;
}

const DetailedBlog: React.FC = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState<Blog | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            if (!id) return;
            try {
                const data = await handleGetBlogById("user", id);
                if (data?.data) setBlog(data.data as Blog);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
    }, [id]);

    if (loading) return null;
    if (!blog) return <div className="h-screen flex items-center justify-center font-Manrope font-black text-gray-400 text-2xl italic">Story not found.</div>;

    const sanitized = DOMPurify.sanitize(blog.description, {
        ADD_ATTR: ["target"],
    });

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white font-Manrope">
                <div className="relative h-[60vh] min-h-[500px] w-full overflow-hidden">
                    <img
                        src={blog.image || landingAsset("stadia.svg")}
                        className="w-full h-full object-cover transform scale-105"
                        alt=""
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
                </div>

                <div className="max-w-4xl mx-auto px-6 -mt-40 relative z-10 pb-24">
                    <div className="bg-white p-12 md:p-20 rounded-[64px] shadow-2xl border border-gray-100">
                        <div className="flex items-center gap-4 mb-10">
                            <span className="px-6 py-2 bg-green-700 text-white rounded-full font-black text-[10px] uppercase tracking-widest">Inspiration</span>
                            <div className="h-0.5 flex-1 bg-gray-100" />
                            <p className="text-gray-400 font-bold italic">{new Date(blog.created_at).toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" })}</p>
                        </div>

                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-gray-900 uppercase tracking-tighter leading-[0.85] mb-12">
                            {blog.title}
                        </h1>

                        <div className="prose prose-xl prose-green max-w-none">
                            <div
                                className="font-Manrope text-gray-600 leading-[1.8] text-xl font-medium italic"
                                dangerouslySetInnerHTML={{ __html: sanitized }}
                            />
                        </div>

                        <div className="mt-20 pt-16 border-t border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-10">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 bg-green-700 rounded-full flex items-center justify-center text-white text-2xl font-black shadow-lg shadow-green-200">S</div>
                                <div>
                                    <p className="font-black text-gray-900 text-lg uppercase tracking-widest">SACA Press</p>
                                    <p className="text-gray-400 font-bold italic uppercase tracking-widest text-[10px]">Official Correspondent</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                {["Twitter", "Facebook", "Copy Link"].map(p => (
                                    <button key={p} className="px-6 py-3 bg-gray-50 border border-gray-100 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-400 hover:text-green-700 hover:border-green-700 transition-all">{p}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Banner />
            <Footer />
        </>
    );
};

export default DetailedBlog;
